import { breakpoints, spacing } from '@naf/theme';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Text } from '@naf/text';
import { ScrollList } from '@components/scroll-list/ScrollList';
import { isNotTransientProp } from '@lib/shouldForwardProp';
import { BlockHeader } from './blocks/MultiRowArticleBlock';

/**
 * The styled primitives of `ColumnBlock`, split out so other pages can target them in their own
 * CSS without importing the block.
 *
 * Three page style sheets do `${ColumnBlockHeader} { … }` to nest rules — they need the generated
 * class name and nothing else. While these lived in `ColumnBlock.tsx` that import dragged in the
 * component, and with it `./index` — the SectionBlocks barrel holding every lazy block definition.
 * Rolldown reported the consequence directly:
 *
 *   [INEFFECTIVE_DYNAMIC_IMPORT] SectionBlocks/ColumnBlock.tsx is dynamically imported by
 *   SectionBlocks/index.tsx but also statically imported by … 4 files …, dynamic import will not
 *   move module into another chunk.
 *
 * Splitting the primitives out here fixes the style-sheet importers — same shape as the Cloudinary
 * video player, a value import reaching for the module behind it. The two pages that only ever wanted
 * the class names, TwoColumnApp (`TwoColumnApp.styles.ts`) and TestedProductContent
 * (`.../TestedProductContent/Styles.tsx`), now import from this file and no longer pull the catalogue.
 *
 * It does NOT yet make `ColumnBlock` splittable. `ModelDetailPage.tsx` still imports the block
 * *component* directly (`@components/SectionBlocks/ColumnBlock`) and renders it synchronously, so that
 * page keeps `ColumnBlock.tsx` — and through it `./index` — statically pinned and the
 * INEFFECTIVE_DYNAMIC_IMPORT persists (now from that single file, not four). Full splittability needs
 * a follow-up: repointing ModelDetailPage at the code-split block instead of the direct module.
 */

export const ColumnBlockWrapper = styled.div``;

export const ColumnWrapper = styled(ScrollList)`
  display: flex;
  justify-content: space-between;
  overflow-y: hidden;

  /* Overflow clipping cuts off the cards' outside box-shadow ring; pad it back in and cancel out with margin. */
  padding: ${spacing.space4};
  margin: -${spacing.space4};

  @media (max-width: ${breakpoints.m}) {
    overflow-x: scroll;
    max-width: 100vw;
    gap: ${spacing.space24};
  }
`;

export const ColumnBlockHeader = styled(BlockHeader, { shouldForwardProp: isNotTransientProp })<{
  $headerVariant: 'large' | 'small';
}>`
  ${({ $headerVariant }) =>
    $headerVariant === 'large' &&
    css`
      margin: 0;
      margin-bottom: ${spacing.space48};
      @media (max-width: ${breakpoints.m}) {
        margin-bottom: ${spacing.space40};
      }
    `}
`;

export const IngressText = styled(Text)`
  margin-top: -${spacing.space16};
  margin-bottom: ${spacing.space24};
`;

export const ColumnContainer = styled.div`
  display: flex;
  flex-flow: column;
  flex-basis: 0;
  flex-grow: 1;

  @media (min-width: ${breakpoints.m}) {
    & > * + * {
      margin-top: ${spacing.space80};
    }
  }
`;
